// frontend/src/components/Samples/SizeTables.jsx

import React, { useState, useEffect } from 'react';
import { horzGroupsAPI, horzValuesAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { FiEdit2, FiTrash2, FiPlus, FiList } from 'react-icons/fi';
import DataTable from '../Common/DataTable';
import Pagination from '../Common/Pagination';
import MasterDataModal from '../Common/MasterDataModal';
import SizesModal from './SizesModal';
import '../Common/MasterDataManager.css';

const SizeTables = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Sizes modal
  const [showSizesModal, setShowSizesModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);

  // Sizes data for display
  const [sizesData, setSizesData] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await horzGroupsAPI.getAll();
      console.log("horzGroupsAPI: ",response.data );
      
      if (response.data.success) {
        setData(response.data.data);
        // Load sizes for each table
        await loadAllSizes(response.data.data);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(t('messages.error_load', 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const loadAllSizes = async (tables) => {
    const sizesMap = {};

    console.log("loadAllSizes");
    
    for (const table of tables) {
      try {
        const response = await horzValuesAPI.getAll(table.horz_group);
        console.log("horzValuesAPI: ",response.data );

        if (response.data.success) {
          const sortedSizes = response.data.data.sort((a, b) => a.sorting - b.sorting);
          sizesMap[table.horz_group] = sortedSizes.map(s => s.horz_value).join(', ');
        }
      } catch (err) {
        console.error(`Error loading sizes for ${table.horz_group}:`, err);
        sizesMap[table.horz_group] = '';
      }
    }
    
    setSizesData(sizesMap);
  };

  const handleCreate = () => {
    setSelectedItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(t('messages.confirm_delete', `Are you sure you want to delete "${item.horz_group}"?`))) {
      return;
    }
    
    try {
      const response = await horzGroupsAPI.delete(item.horz_group_id);
      if (response.data.success) {
        alert(t('messages.success_delete', 'Deleted successfully'));
        loadData();
      }
    } catch (err) {
      alert(err.response?.data?.error || t('messages.error_delete', 'Failed to delete'));
    }
  };

  const handleManageSizes = (item) => {
    setSelectedTable(item);
    setShowSizesModal(true);
  };

  const handleModalClose = (shouldReload) => {
    setShowModal(false);
    setSelectedItem(null);
    if (shouldReload) {
      loadData();
      setCurrentPage(1);
    }
  };

  const handleSizesModalClose = () => {
    setShowSizesModal(false);
    setSelectedTable(null);
    // Reload to refresh sizes column
    loadData();
  };

  // Client-side filtering
  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      item.horz_group.toLowerCase().includes(search) ||
      item.description?.toLowerCase().includes(search) ||
      sizesData[item.horz_group]?.toLowerCase().includes(search)
    );
  });

  // Pagination
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const columns = [
    {
      header: t('size_tables.code', 'Table Code'),
      field: 'horz_group',
      className: 'code-column'
    },
    {
      header: t('size_tables.description', 'Description'),
      field: 'description'
    },
    {
      header: t('sizes.title', 'Sizes'),
      field: 'sizes',
      sortable: false,
      render: (row) => (
        <span className="sizes-list-text">
          {sizesData[row.horz_group] || <em style={{ color: 'var(--gray-400)' }}>No sizes</em>}
        </span>
      )
    },
    {
      header: t('created_at', 'Created'),
      field: 'created_at',
      render: (row) => new Date(row.created_at).toLocaleDateString(),
      sortable: false
    },
    {
      header: t('actions', 'Actions'),
      field: 'actions',
      sortable: false,
      render: (row) => (
        <div className="action-buttons">
          <button 
            onClick={() => handleManageSizes(row)}
            className="btn-action btn-view"
            title={t('sizes.manage', 'Manage Sizes')}
          >
            <FiList size={16} />
          </button>
          <button 
            onClick={() => handleEdit(row)}
            className="btn-action btn-edit"
            title={t('edit', 'Edit')}
          >
            <FiEdit2 size={16} />
          </button>
          <button 
            onClick={() => handleDelete(row)}
            className="btn-action btn-delete"
            title={t('delete', 'Delete')}
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      )
    }
  ];

  const formFields = [
    {
      name: 'horzGroup',
      label: 'size_tables.code',
      type: 'text',
      required: true,
      maxLength: 20
    },
    {
      name: 'description',
      label: 'size_tables.description',
      type: 'text',
      maxLength: 50
    },
    {
      name: 'notes',
      label: 'notes',
      type: 'textarea',
      rows: 3
    }
  ];

  return (
    <div className="master-data-page">
      <div className="master-data-header">
        <div>
          <h2>{t('size_tables.title', 'Size Tables')}</h2>
          <p className="master-data-subtitle">{t('size_tables.subtitle', 'Manage size tables')}</p>
        </div>
        <button onClick={handleCreate} className="btn-primary">
          <FiPlus size={16} style={{ marginRight: '6px' }} />
          {t('size_tables.create', 'Create Size Table')}
        </button>
      </div>

      <div className="master-data-filters">
        <input
          type="text"
          placeholder={t('search.placeholder', 'Search...')}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="search-input"
        />
        <span className="result-count">
          {filteredData.length} {t('records_found', 'records found')}
        </span>
      </div>

      <DataTable
        columns={columns}
        data={paginatedData}
        loading={loading}
        error={error}
        onRetry={loadData}
        emptyMessage={t('no_data', 'No data available')}
      />

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      {showModal && (
        <MasterDataModal
          title={selectedItem ? t('edit_item', 'Edit') : t('create_item', 'Create')}
          item={selectedItem}
          fields={formFields}
          idField="horz_group_id"
          api={horzGroupsAPI}
          onClose={handleModalClose}
        />
      )}

      {showSizesModal && selectedTable && (
        <SizesModal
          sizeTable={selectedTable}
          onClose={handleSizesModalClose}
        />
      )}
    </div>
  );
};

export default SizeTables;